import React, { useCallback, useEffect, useRef, useState } from "react";

import Headshot from "components/general/Headshot";
import PillButton from "components/general/PillButton";
// import iconContract from "images/ic_expand_less.png";
import iconExpand from "images/ic_expand_more.png";
import styled from "styled-components";
import theme from "styles/theme";

const Container = styled.div`
  max-width: 408px;
  box-sizing: border-box;

  border-radius: 16px;
  background: ${(props) => props.$backgroundColor};
  padding: ${theme.spacing.unit * 2}px;
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.unit * 2}px;
`;

const HeadshotContainer = styled.div`
  padding: 4px;
`;

const SpeakerContainer = styled.div`
  display: flex;
  column-gap: 8px;
`;

const SpeakerInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: 4px;
`;

const SpeakerName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const SpeakerTitle = styled.div`
  font-size: 14px;
`;

const QuoteContainer = styled.div`
  transition: height 0.3s ease;
  overflow: hidden;

  height: ${(props) => props.$height}px;
`;

const Quote = styled.div`
  color: #323232;

  font-family: Inter;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 28px */

  ${(props) => {
    if (props.$isExpanded) {
      return `
        -webkit-line-clamp: unset; /* Number of lines */
      `;
    } else {
      return `
        display: -webkit-box;
        -webkit-line-clamp: 2; /* Number of lines */
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      `;
    }
  }}
`;

const BoldedSpan = styled.span`
  font-weight: bold;
  color: ${(props) => props.$color};
`;

const ShowMoreOrLessButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

function getStyledTextSpans(
  boldColor,
  text,
  boldWordRangeStart,
  boldWordRangeEnd
) {
  const words = text.split(" "); // Split text into words
  const spans = [];

  words.forEach((word, index) => {
    if (index >= boldWordRangeStart && index <= boldWordRangeEnd) {
      // Add bolded word
      spans.push(
        <BoldedSpan key={index} $color={boldColor}>
          {word}
        </BoldedSpan>
      );
    } else {
      // Add regular word
      spans.push(<span key={index}>{word}</span>);
    }

    // Add space after each word except the last one
    if (index < words.length - 1) {
      spans.push(" ");
    }
  });

  return spans;
}

const TeamQuote = ({ jobClipHighlight, brandingData, onExpandClick }) => {
  const quoteRef = useRef(null);
  const [isQuoteRefSet, setIsQuoteRefSet] = useState(false);
  const [quoteHeight, setQuoteHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const setQuoteRef = useCallback((node) => {
    if (!node) return;

    quoteRef.current = node;
    setIsQuoteRefSet(true);
  }, []);

  useEffect(() => {
    if (!isQuoteRefSet || !quoteRef.current) return;

    const updateHeight = () => {
      if (quoteRef.current) {
        setQuoteHeight(quoteRef.current.scrollHeight);
      }
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(quoteRef.current);

    // Initial height update
    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [jobClipHighlight, isQuoteRefSet]);

  const handleShowMoreClick = () => {
    setIsExpanded(true);

    if (onExpandClick) {
      onExpandClick();
    }
  };

  const handleShowLessClick = () => {
    setIsExpanded(false);
  };

  const firstNonHostSpeaker = jobClipHighlight.speakers.find(
    (speaker) => !speaker.isHost
  );

  let quote = jobClipHighlight.transcriptText;

  if (jobClipHighlight.boldWordRangeEnd) {
    quote = getStyledTextSpans(
      brandingData.accentTextColor,
      jobClipHighlight.transcriptText,
      jobClipHighlight.boldWordRangeStart - jobClipHighlight.wordRangeStart,
      jobClipHighlight.boldWordRangeEnd - jobClipHighlight.wordRangeStart
    );
  }

  return (
    <Container $backgroundColor={brandingData.secondaryColor}>
      <SpeakerContainer>
        <HeadshotContainer>
          <Headshot
            src={firstNonHostSpeaker.headshotPhotoUrl}
            speaker={firstNonHostSpeaker}
            size={56}
            borderWidth={0}
          />
        </HeadshotContainer>
        <SpeakerInfoContainer>
          <SpeakerName>{firstNonHostSpeaker.firstName}</SpeakerName>
          <SpeakerTitle>{firstNonHostSpeaker.title}</SpeakerTitle>
        </SpeakerInfoContainer>
      </SpeakerContainer>

      <QuoteContainer
        $isExpanded={isExpanded}
        $height={isExpanded ? quoteHeight : 56}
      >
        <Quote $isExpanded={isExpanded} ref={setQuoteRef}>
          {quote}
        </Quote>
      </QuoteContainer>
      <ShowMoreOrLessButtonContainer>
        <PillButton
          isSmall
          isBrandedGlow
          brandingData={brandingData}
          iconTrailing={iconExpand}
          iconTrailingTransform={
            isExpanded ? "rotate(-180deg)" : "rotate(0deg)"
          }
          onClick={isExpanded ? handleShowLessClick : handleShowMoreClick}
        >
          {isExpanded ? "Show less" : "Show more"}
        </PillButton>
      </ShowMoreOrLessButtonContainer>
    </Container>
  );
};

export default TeamQuote;
